import { defineContent, type DefinedContent } from '@post-office/define-content';

/**
 * Combined content for the Rovo extension install CTA left-nav placement.
 *
 * Each copy variation is a config set. Every set exposes the same fields so `render.tsx` can read a
 * field whichever unit resolved: the variant-specific `heading`/`description` plus the shared link,
 * icon-alt and dismiss strings. `default` mirrors the `copy-version-b` variation, which is the
 * fallback cohort. Descriptors are written inline because values pulled from a variable or spread
 * are invisible to the i18n extractor.
 */
export const registeredContent: DefinedContent<{
	default: [
		'heading',
		'description',
		'linkText',
		'jiraLinkText',
		'jiraRovoLinkText',
		'rovoIconAlt',
		'chromeIconAlt',
		'jiraIconAlt',
		'dismissLabel',
	];
	'copy-version-b': ['heading', 'description'];
	'copy-version-c': ['heading', 'description'];
	'jira-take-anywhere': ['heading', 'description'];
	'jira-rovo': ['heading', 'description'];
}> = defineContent({
	default: {
		heading: {
			id: 'rovo-extension-install-cta-left-nav.heading.copy-version-b',
			defaultMessage: 'Get answers for work, anywhere',
			description: 'Heading text for rovo extension install cta left nav',
			role: 'title',
		},
		description: {
			id: 'rovo-extension-install-cta-left-nav.description.copy-version-b',
			defaultMessage:
				'Ask about your projects, work policies, or anything about your company. Rovo Chat from any tab.',
			description: 'Description text for rovo extension install cta left nav',
			role: 'description',
		},
		linkText: {
			id: 'rovo-extension-install-cta-left-nav.link',
			defaultMessage: 'Get the browser extension',
			description: 'Text for rovo extension install cta link left nav',
		},
		jiraLinkText: {
			id: 'rovo-extension-install-cta-left-nav.jira-link',
			defaultMessage: 'Get the browser extension',
			description: 'Text for Jira left nav extension install CTA link',
		},
		jiraRovoLinkText: {
			id: 'rovo-extension-install-cta-left-nav.jira-rovo-link',
			defaultMessage: 'Get the Rovo extension',
			description: 'Text for Jira left nav extension install CTA link (Rovo variant)',
		},
		rovoIconAlt: {
			id: 'rovo-extension-install-cta-left-nav.rovo-icon-alt',
			defaultMessage: 'Rovo Icon',
			description: 'Alt text for Rovo icon image',
		},
		chromeIconAlt: {
			id: 'rovo-extension-install-cta-left-nav.chrome-icon-alt',
			defaultMessage: 'Google Chrome Icon',
			description: 'Alt text for Google Chrome icon image',
		},
		jiraIconAlt: {
			id: 'rovo-extension-install-cta-left-nav.jira-icon-alt',
			defaultMessage: 'Jira Icon',
			description: 'Alt text for Jira icon image',
		},
		dismissLabel: {
			id: 'rovo-extension-install-cta-left-nav.dismiss-label',
			defaultMessage: 'Dismiss message',
			description: 'Label for dismiss button',
		},
	},
	'copy-version-b': {
		heading: {
			id: 'rovo-extension-install-cta-left-nav.heading.copy-version-b',
			defaultMessage: 'Get answers for work, anywhere',
			description: 'Heading text for rovo extension install cta left nav',
			role: 'title',
		},
		description: {
			id: 'rovo-extension-install-cta-left-nav.description.copy-version-b',
			defaultMessage:
				'Ask about your projects, work policies, or anything about your company. Rovo Chat from any tab.',
			description: 'Description text for rovo extension install cta left nav',
			role: 'description',
		},
	},
	'copy-version-c': {
		heading: {
			id: 'rovo-extension-install-cta-left-nav.heading.copy-version-c',
			defaultMessage: 'Unlock Rovo in every tab',
			description: 'Heading text for rovo extension install cta left nav',
			role: 'title',
		},
		description: {
			id: 'rovo-extension-install-cta-left-nav.description.copy-version-c',
			defaultMessage: '50K+ workers use it to chat with AI that understands their company.',
			description: 'Description text for rovo extension install cta left nav',
			role: 'description',
		},
	},
	'jira-take-anywhere': {
		heading: {
			id: 'rovo-extension-install-cta-left-nav.heading.jira-take-anywhere',
			defaultMessage: 'Jira. Anywhere.',
			description: 'Heading text for Jira left nav extension install CTA',
			role: 'title',
		},
		description: {
			id: 'rovo-extension-install-cta-left-nav.description.jira-take-anywhere',
			defaultMessage:
				'Manage work items, get instant insights, and act on Jira from any tab in your browser.',
			description: 'Description text for Jira left nav extension install CTA',
			role: 'description',
		},
	},
	'jira-rovo': {
		heading: {
			id: 'rovo-extension-install-cta-left-nav.heading.jira-rovo',
			defaultMessage: 'Jira. Anywhere.',
			description: 'Heading text for Jira left nav Rovo extension variant CTA',
			role: 'title',
		},
		description: {
			id: 'rovo-extension-install-cta-left-nav.description.jira-rovo',
			defaultMessage:
				'Manage work items, get instant insights, and act on Jira from any tab in your browser.',
			description: 'Description text for Jira left nav Rovo extension variant CTA',
			role: 'description',
		},
	},
});

export const COPY_VARIATIONS: {
	'copy-version-b': (typeof registeredContent)['copy-version-b'];
	'copy-version-c': (typeof registeredContent)['copy-version-c'];
	'jira-take-anywhere': (typeof registeredContent)['jira-take-anywhere'];
	'jira-rovo': (typeof registeredContent)['jira-rovo'];
} = {
	'copy-version-b': registeredContent['copy-version-b'],
	'copy-version-c': registeredContent['copy-version-c'],
	'jira-take-anywhere': registeredContent['jira-take-anywhere'],
	'jira-rovo': registeredContent['jira-rovo'],
};
